<template>
  <form class="boxForm_step8" @submit.prevent="onSubmit">
    <div class="inputList contact-form">
      <BaseInputPhone
        ref="phoneInput"
        v-if="phoneInfo_ !== undefined"
        :error="$v.phoneInfo_.phone.$dirty && $v.phoneInfo_.phone.$invalid"
        :valid="!$v.phoneInfo_.phone.$invalid"
        :default-country-code="phoneInfo_.countryCode"
        v-model="phoneInfo_.phone"
        @update="onUpdatePhone"
        @phone-number-blur="$emit('blurPhone', phoneInfo_)"
        :excluded-countries="excludedCountries"
      />
      <BaseInput
        ref="emailInput"
        v-if="email_ !== undefined"
        :placeholder="$t('qic_user_contacts_email')"
        :validation="$v.email_"
        name="email"
        inputStyle="big"
        :value="email_ | decodeEmail"
        :trim="true"
        :mask="$options.emailMask"
        @input="onInput"
        @blur="$emit('blurEmail', email_)"
      >
        <template v-slot:invalid-input-error>
          {{ emailErrorMessage || $t('qic_error_invalid_input') }}
        </template>
      </BaseInput>
      <template v-if="promocode_ !== undefined">
        <PromoInputWithApply
          v-if="promocodeValidation"
          v-model="promocode_"
          :validation="promocodeValidation"
          class="contact-form__promocode"
          @focus="$emit('focusPromocode')"
          @blur="$emit('blurPromocode', $event)"
          @clickPromocode="$emit('togglePromocode', $event)"
        />
        <BasePromocodeInput
          v-else
          v-model="promocode_"
          @focus="$emit('focusPromocode')"
          @blur="$emit('blurPromocode', $event)"
        />
      </template>
      <div class="checkbox-container">
        <input v-model="acceptance"
          type="checkbox"
          class="checkbox"
          id="acceptCheck"
          name="acceptCheck">
          <label for="acceptCheck">{{ $t('qic_user_contacts_acceptance') }}</label>
      </div>
    </div>
    <BaseButton :symbol="loading ? 'no' : 'next'">
      <Loader v-if="loading" class="contacts-loader" />
      <template v-else>{{ $t('qic_proceed') }}</template>
    </BaseButton>
    <Reminder v-if="!$slots['privacy-policy']" />
    <slot name="privacy-policy"> </slot>
  </form>
</template>

<script>
import { required, email, helpers } from 'vuelidate/lib/validators'
import BaseInputPhone from '../inputs/BaseInputPhone'
import PromoInputWithApply from '../inputs/PromoInputWithApply'
import BasePromocodeInput from '../inputs/BasePromocodeInput'
import Loader from '../Loader.vue'
import Reminder from '../Reminder.vue'
const emailToken = /^[a-zA-Z0-9_\-@.+]/
const EMAIL_LENGTH = 100
export default {
  name: 'UserContactsForm',
  components: {
    BaseInputPhone,
    PromoInputWithApply,
    BasePromocodeInput,
    Loader,
    Reminder
  },
  emailMask: new Array(EMAIL_LENGTH).fill(emailToken),
  props: {
    email: { type: String },
    emailRegex: { type: RegExp },
    emailErrorMessage: { type: String, default: '' },
    phoneInfo: { type: Object },
    promocode: { type: String },
    promocodeValidation: { type: Function },
    excludedCountries: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  emits: [
    'onSubmit', 'focusPromocode', 'blurPromocode', 'togglePromocode',
    'blurPhone', 'blurEmail', 'error', 'email'
  ],
  data () {
    return {
      phoneInfo_: this.phoneInfo,
      email_: this.email,
      promocode_: this.promocode,
      acceptance: true
    }
  },
  filters: {
    decodeEmail: (value) => {
      if (!value) {
        return ''
      }
      value = value.toString()
      return value.replace('%2b', '+')
    }
  },
  methods: {
    onSubmit () {
      if (this.loading) {
        return
      }

      this.$v.$touch()
      const decodedEmail = this.email_?.toLowerCase().replaceAll('+', '%2b') ?? ''
      if (this.submitDisabled()) {
        const phoneInput = this.$refs.phoneInput
        const emailInput = this.$refs.emailInput
        const errors = {}
        if (this.$v.phoneInfo_?.$invalid) {
          const message = this.getErrorMessage(phoneInput)
          errors.phone = message
        }
        if (this.$v.email_?.$invalid) {
          const message = this.getErrorMessage(emailInput)
          errors.email = message
        }
        this.$emit('error', errors)
        return
      }
      this.$emit('onSubmit', {
        email: decodedEmail,
        promocode: this.promocode_,
        phoneInfo: this.phoneInfo_,
        acceptance: this.acceptance
      })
    },
    getErrorMessage (inputComponent) {
      return inputComponent.value
        ? inputComponent.invalidErrorMessage
        : inputComponent.requireErrorMessage
    },
    onUpdatePhone (result) {
      this.phoneInfo_.formattedPhone = result?.formattedNumber()
      this.phoneInfo_.isValidLength = result?.isValidLength()
      this.phoneInfo_.countryCode = result?.countryCode
      this.$v.phoneInfo_.$reset()
    },
    submitDisabled () {
      return this.$v.$invalid
    },
    onInput (value) {
      this.email_ = value
      this.$emit('email', value)
    }
  },
  validations () {
    const validations = {}
    if (this.email_ !== undefined) {
      const regex = this.emailRegex ? helpers.regex('regex', this.emailRegex) : () => true

      validations.email_ = {
        required,
        email,
        regex,
        message () {
          return !this.emailErrorMessage
        }
      }
    }
    if (this.phoneInfo_ !== undefined) {
      validations.phoneInfo_ = {
        phone: {
          validPhone: () => this.phoneInfo_.isValidLength || false
        }
      }
    }
    return validations
  }
}
</script>

<style lang="scss">
.contacts-loader svg circle {
  stroke: #fff !important;
}
</style>
